import JsonLd from '@/components/base/JsonLd';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function TrendingGuide() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://allchinabuyspreadsheet.wiki';
  const pagePath = '/allchinabuy-sheet-trending';
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: "AllChinaBuySpreadsheet Trending: What's Hot in China Products Right Now",
    description: "Discover what's trending across all 14 AllChinaBuySpreadsheet categories. Real-time popularity data tracking purchase velocity, repeat buyer rates, and social media buzz for the hottest China products.",
    author: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet' },
    publisher: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet', url: siteUrl },
    datePublished: '2026-06-25',
    dateModified: '2026-06-25',
    mainEntityOfPage: { '@type': 'WebPage', '@id': siteUrl + pagePath },
    url: siteUrl + pagePath,
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'AllChinaBuySpreadsheet Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Trending Products', item: siteUrl + pagePath },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <header className="relative h-[420px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Trending%20fashion%20items%20displayed%20on%20modern%20retail%20shelves%2C%20vibrant%20colors%2C%20spotlight%20illumination%2C%20trend%20concept%20photography%2C%20contemporary%20boutique%20setting%2C%20dynamic%20energy%2C%20popular%20products%20showcase%2C%20social%20media%20worthy%20aesthetic&width=1800&height=840&seq=trending-hero-v3&orientation=landscape"
          alt="AllChinaBuySpreadsheet trending products"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
        <div className="relative z-10 w-full px-6 md:px-10 pt-16">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-3 py-1 bg-white/15 backdrop-blur-sm text-xs font-medium text-white rounded-full uppercase tracking-[0.15em] mb-5">Trending</span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">AllChinaBuySpreadsheet Trending: What's Hot in China Products Right Now</h1>
          </div>
        </div>
      </header>
      <article className="py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <p className="text-lg text-foreground-700 leading-relaxed mb-8">
            Trends in Chinese product markets move fast — what is popular this month may be gone next month. The
            <strong className="text-primary-600 font-semibold"> AllChinaBuySpreadsheet</strong> trending section
            tracks real-time popularity data across all 14 categories, giving you a pulse on what global buyers are
            actually purchasing right now.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">How We Identify Trending Products</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            Our trending algorithm analyzes multiple signals: purchase velocity (how quickly products sell after being
            listed), repeat buyer rates, search-to-purchase conversion ratios, and social media mention frequency. The
            <strong className="text-primary-600 font-semibold"> best china spreadsheet products</strong> trending list
            updates every 48 hours, ensuring you are always seeing fresh, relevant data rather than stale recommendations.
          </p>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            What makes the <strong className="text-primary-600 font-semibold">allchinabuy spreadsheet</strong> trending
            section particularly valuable is category-specific trending. A hot item in Accessories may have completely
            different momentum characteristics than a trending piece in Shoes. Our category-level trending data lets
            you drill down to exactly what matters for your interests.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">Current Trending Categories</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            As of the latest update, Hoodies/Sweaters and Accessories are showing the strongest upward momentum, driven
            by seasonal shifts and influencer-driven demand. The <strong className="text-primary-600 font-semibold">
            china product spreadsheet list</strong> for these categories shows verified products with rising sales
            curves and increasing buyer interest week over week.
          </p>
          <div className="mt-14 p-8 bg-primary-50 rounded-2xl text-center">
            <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">See What's Trending Now</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">
                Explore Trending <i className="ri-arrow-right-line"></i>
              </a>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-200 text-sm font-medium text-foreground-700 rounded-full hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer">
                Back to Home <i className="ri-home-4-line"></i>
              </Link>
            </div>
          </div>
          <nav className="mt-12 pt-8 border-t border-background-200/70">
            <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-4">Related Articles</p>
            <ul className="space-y-2">
              <li><Link to="/allchinabuy-sheet-pricing" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Pricing Strategy: Best Deals Guide &rarr;</Link></li>
              <li><Link to="/allchinabuy-sheet-guide" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Shopping Guide: Save 70% &rarr;</Link></li>
              <li><Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer">&larr; AllChinaBuySpreadsheet Home</Link></li>
            </ul>
          </nav>
        </div>
      </article>
      <Footer />
    </div>
  );
}